exports.clamp = function (x, y) {
    return x <= y ? x : y;
};

exports.shade = function (col, amt) {
    var usePound = false;
    if (col[0] === "#") {
        col = col.slice(1);
        usePound = true;
    }
    var num = parseInt(col, 16);

    var r = (num >> 16) + amt;

    if (r > 255) r = 255;
    else if (r < 0) r = 0;

    var b = ((num >> 8) & 0x00ff) + amt;

    if (b > 255) b = 255;
    else if (b < 0) b = 0;

    var g = (num & 0x0000ff) + amt;

    if (g > 255) g = 255;
    else if (g < 0) g = 0;

    return (usePound ? "#" : "") + (g | (b << 8) | (r << 16)).toString(16);
};

export const brightness = function (r, g, b) {
    return (r * 299 + g * 587 + b * 114) / 1000;
};

/**
 * Returns whether the colour is considered to be dark.
 * Roughly based on https://www.w3.org/TR/AERT/#color-contrast
 * @param {string} color As a Hex value
 * @param {string} lightChoice
 * @param {string} darkChoice
 *
 * @returns {string} The contrasting color
 */
export const isDarkColor = (color) => {
    const colorObject = hexToRgb(color);
    const colorBrightness = brightness(
        colorObject.r,
        colorObject.g,
        colorObject.b
    );

    if (colorBrightness < 123) {
        return true;
    }
    return false;
};

/**
 * Returns either a light or dark colour to contrast as much as possible
 * to the colour passed in. Roughly based on https://www.w3.org/TR/AERT/#color-contrast
 * @param {string} color As a Hex value
 * @param {string} lightChoice
 * @param {string} darkChoice
 *
 * @returns {string} The contrasting color
 */
export const getContrastingColor = (
    color,
    lightChoice = "#ffffff",
    darkChoice = "#000000"
) => {
    if (isDarkColor(color) < 123) {
        return lightChoice;
    }
    return darkChoice;
};

export function hexToHSL(hex) {
    var result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    var r = parseInt(result[1], 16);
    var g = parseInt(result[2], 16);
    var b = parseInt(result[3], 16);
    (r /= 255), (g /= 255), (b /= 255);
    var max = Math.max(r, g, b),
        min = Math.min(r, g, b);
    var h,
        s,
        l = (max + min) / 2;
    if (max == min) {
        h = s = 0; // achromatic
    } else {
        var d = max - min;
        s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
        switch (max) {
            case r:
                h = (g - b) / d + (g < b ? 6 : 0);
                break;
            case g:
                h = (b - r) / d + 2;
                break;
            case b:
                h = (r - g) / d + 4;
                break;
        }
        h /= 6;
    }
    var HSL = new Object();
    HSL["h"] = h;
    HSL["s"] = s;
    HSL["l"] = l;
    return HSL;
}

export function hexToRgb(hex) {
    if (hex.length < 5) {
        hex += hex.slice(1);
    }
    var result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    if (!result) {
        return null;
    }
    return result
        ? {
              r: parseInt(result[1], 16),
              g: parseInt(result[2], 16),
              b: parseInt(result[3], 16),
          }
        : null;
}

export function hexToRgbaString(hex, alpha = 1) {
    const rgbObject = hexToRgb(hex);
    if (!rgbObject) {
        return hex;
    }
    return `rgba(
        ${rgbObject.r}, ${rgbObject.g}, ${rgbObject.b}, ${alpha}
    )`;
}

export function HSLToHex(hsl) {
    var h = hsl.h,
        s = hsl.s,
        l = hsl.l;
    var r, g, b;
    if (s === 0) {
        r = g = b = l; // achromatic
    } else {
        const hue2rgb = (p, q, t) => {
            if (t < 0) t += 1;
            if (t > 1) t -= 1;
            if (t < 1 / 6) return p + (q - p) * 6 * t;
            if (t < 1 / 2) return q;
            if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
            return p;
        };
        const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
        const p = 2 * l - q;
        r = hue2rgb(p, q, h + 1 / 3);
        g = hue2rgb(p, q, h);
        b = hue2rgb(p, q, h - 1 / 3);
    }
    const toHex = (x) => {
        const hex = Math.round(x * 255).toString(16);
        return hex.length === 1 ? "0" + hex : hex;
    };
    return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

export function foregroundColor(hex, lightness, lightColor, darkColor) {
    return hexToHSL(hex).l < lightness ? lightColor : darkColor;
}

export function luminanace(hex) {
    var rgb = hexToRgb(hex);

    var a = [rgb.r, rgb.g, rgb.b].map(function (v) {
        v /= 255;
        return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
    });

    return a[0] * 0.2126 + a[1] * 0.7152 + a[2] * 0.0722;
}

export function adjustLightness(hex, amount) {
    var hsl = hexToHSL(hex);
    const newLightness = hsl.l + amount / 100;
    hsl.l = newLightness > 0.7 ? 0.7 : newLightness < 0 ? 0 : newLightness;
    var hex = HSLToHex(hsl);
    return hex;
}

export function darken(hex, amount) {
    var hsl = hexToHSL(hex);
    hsl.l = hsl.l * amount;
    var hex = HSLToHex(hsl);
    return hex;
}

export function lighten(hex, amount) {
    let hsl = hexToHSL(hex);
    hsl.l = hsl.l * amount;
    return HSLToHex(hsl);
}

export const isColor = (strColor) => {
    const s = new Option().style;
    s.color = strColor;
    return s.color !== "";
};

export const isBrowser = new Function(
    "try {return this===window;}catch(e){ return false;}"
);

export function transparentize(hex, amount) {
    const rgb = hexToRgb(hex);

    return `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${1 - amount})`;
}
